import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PartnerDashboardDateRange } from "../lib/partnerDashboardDate";
import {
  fetchPartnerAnalyticsMoney,
  fetchPartnerAnalyticsSummary,
  fetchPartnerClientDriverActivity,
  fetchPartnerClientOrders,
  fetchPartnerClientReport,
  fetchPartnerClientsOverview,
  fetchPartnerClientStatement,
  fetchPartnerClientWallet,
  fetchPartnerWallet,
  rangeToInstants,
  setPartnerClientWalletLimit,
  settlePartnerLedgerEntry,
  topupPartnerClientWallet,
} from "../lib/partnerReports";
import type { PartnerOrderStage } from "../lib/partnerOrders";
import { usePartnerSessionStore } from "../lib/partnerAuth";

function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

/** Range part of a query key — instants so key === fetch parameters. */
function rangeKey(range: PartnerDashboardDateRange): [string, string] {
  const { fromIso, toIso } = rangeToInstants(range);
  return [fromIso, toIso];
}

export function usePartnerClientsOverview(range?: PartnerDashboardDateRange) {
  const partnerId = usePartnerId();
  const rangePart = range ? rangeKey(range) : (["all", "all"] as [string, string]);
  return useQuery({
    queryKey: ["partner-clients-overview", partnerId, ...rangePart],
    enabled: !!partnerId,
    queryFn: () => fetchPartnerClientsOverview(partnerId!, range),
    refetchInterval: 30_000,
  });
}

export function usePartnerClientReport(
  tenantId: string | null,
  range: PartnerDashboardDateRange
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  return useQuery({
    queryKey: ["partner-client-report", partnerId, tenantId, fromIso, toIso],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => fetchPartnerClientReport(partnerId!, tenantId!, range),
    refetchInterval: 60_000,
  });
}

export function usePartnerClientDriverActivity(
  tenantId: string | null,
  range: PartnerDashboardDateRange
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  return useQuery({
    queryKey: [
      "partner-client-driver-activity",
      partnerId,
      tenantId,
      fromIso,
      toIso,
    ],
    enabled: !!partnerId && !!tenantId,
    queryFn: () =>
      fetchPartnerClientDriverActivity(partnerId!, tenantId!, range),
    refetchInterval: 60_000,
  });
}

export function usePartnerClientOrders(
  tenantId: string | null,
  range: PartnerDashboardDateRange,
  opts?: {
    stage?: PartnerOrderStage | "all";
    fulfillment?: "delivery" | "pickup" | "all";
    query?: string;
    page?: number;
    pageSize?: number;
  }
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  const page = opts?.page ?? 0;
  const pageSize = opts?.pageSize ?? 25;
  const stage = opts?.stage ?? "all";
  const fulfillment = opts?.fulfillment ?? "all";
  const query = opts?.query?.trim() ?? "";
  return useQuery({
    queryKey: [
      "partner-client-orders",
      partnerId,
      tenantId,
      fromIso,
      toIso,
      stage,
      fulfillment,
      query,
      page,
      pageSize,
    ],
    enabled: !!partnerId && !!tenantId,
    queryFn: () =>
      fetchPartnerClientOrders(partnerId!, tenantId!, range, {
        stage,
        fulfillment,
        query,
        limit: pageSize,
        offset: page * pageSize,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function usePartnerClientStatement(
  tenantId: string | null,
  range: PartnerDashboardDateRange
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  return useQuery({
    queryKey: ["partner-client-statement", partnerId, tenantId, fromIso, toIso],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => fetchPartnerClientStatement(partnerId!, tenantId!, range),
    refetchInterval: 60_000,
  });
}

export function usePartnerAnalytics(
  range: PartnerDashboardDateRange,
  opts?: { tenantId?: string | null; fulfillment?: "delivery" | "pickup" | null }
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  const tenantId = opts?.tenantId ?? null;
  const fulfillment = opts?.fulfillment ?? null;
  return useQuery({
    queryKey: ["partner-analytics", partnerId, fromIso, toIso, tenantId, fulfillment],
    enabled: !!partnerId,
    queryFn: () =>
      fetchPartnerAnalyticsSummary(partnerId!, range, { tenantId, fulfillment }),
    refetchInterval: 60_000,
  });
}

export function usePartnerAnalyticsMoney(
  range: PartnerDashboardDateRange,
  opts?: { tenantId?: string | null; fulfillment?: "delivery" | "pickup" | null }
) {
  const partnerId = usePartnerId();
  const [fromIso, toIso] = rangeKey(range);
  const tenantId = opts?.tenantId ?? null;
  const fulfillment = opts?.fulfillment ?? null;
  return useQuery({
    queryKey: [
      "partner-analytics-money",
      partnerId,
      fromIso,
      toIso,
      tenantId,
      fulfillment,
    ],
    enabled: !!partnerId,
    queryFn: () =>
      fetchPartnerAnalyticsMoney(partnerId!, range, { tenantId, fulfillment }),
    refetchInterval: 60_000,
  });
}

export function useSettleLedgerEntry() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ledgerId: string) => {
      if (!partnerId) throw new Error("no_partner");
      return settlePartnerLedgerEntry(partnerId, ledgerId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-analytics"] });
      void qc.invalidateQueries({ queryKey: ["partner-client-statement"] });
      void qc.invalidateQueries({ queryKey: ["partner-clients-overview"] });
      void qc.invalidateQueries({ queryKey: ["partner-wallet"] });
    },
  });
}

export function usePartnerWallet() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-wallet", partnerId],
    enabled: !!partnerId,
    queryFn: () => fetchPartnerWallet(partnerId!),
    refetchInterval: 60_000,
  });
}

export function usePartnerClientWallet(tenantId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-client-wallet", partnerId, tenantId],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => fetchPartnerClientWallet(partnerId!, tenantId!),
    refetchInterval: 60_000,
  });
}

function useInvalidateClientWallet(_tenantId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["partner-client-wallet"] });
    void qc.invalidateQueries({ queryKey: ["partner-client-statement"] });
    void qc.invalidateQueries({ queryKey: ["partner-clients-overview"] });
  };
}

export function useTopupPartnerClientWallet(tenantId: string) {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateClientWallet(tenantId);
  return useMutation({
    mutationFn: (vars: { amount: number; note?: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return topupPartnerClientWallet(partnerId, tenantId, vars.amount, vars.note);
    },
    onSuccess: invalidate,
  });
}

export function useSetPartnerClientWalletLimit(tenantId: string) {
  const partnerId = usePartnerId();
  const invalidate = useInvalidateClientWallet(tenantId);
  return useMutation({
    mutationFn: (negativeLimit: number) => {
      if (!partnerId) throw new Error("no_partner");
      return setPartnerClientWalletLimit(partnerId, tenantId, negativeLimit);
    },
    onSuccess: invalidate,
  });
}
